import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { fetchCrmInstitutes } from '../../../services/crmInstituteService';
import { fetchUsers } from '../../../services/userService';
import { createCrmLead } from '../../../services/crmLeadsService';

interface Institute {
  crm_institute_id: number;
  institute_name: string;
}

interface User {
  user_id: number;
  username: string;
}

const AddLead: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    crm_institute_id: '',
    priority: 'Medium',
    source: 'Referral',
    status: 'Open',
    assigned_to: ''
  });

  const [institutes, setInstitutes] = useState<Institute[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // ✅ New state for API response

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [institutesData, usersData] = await Promise.all([
          fetchCrmInstitutes(),
          fetchUsers()
        ]);
        setInstitutes(institutesData);
        setUsers(usersData);
      } catch (err) {
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccessMessage(null); // Reset previous messages

    try {
      const response = await createCrmLead(formData); // API Call
      setSuccessMessage(response.message); // ✅ Store API message in state
      setTimeout(() => navigate('/crm/leads/all-leads'), 2000); // Redirect after 2s
    } catch (error: any) {
      setError(
        error.response?.data?.message ||
          'Error submitting lead. Please try again.'
      );
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
        <p className="ms-2">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="fw-bold">Add New Lead</h2>

      {/* ✅ Show success message */}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {/* ✅ Show error message */}
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit} className="shadow-lg p-4 rounded">
        {/* Institute Selection */}
        <Form.Group className="mb-3">
          <Form.Label>Institute</Form.Label>
          <Form.Select
            name="crm_institute_id"
            value={formData.crm_institute_id}
            onChange={handleChange}
            required
            disabled={submitting}
          >
            <option value="">Select Institute</option>
            {institutes.map(inst => (
              <option key={inst.crm_institute_id} value={inst.crm_institute_id}>
                {inst.institute_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Priority Selection */}
        <Form.Group className="mb-3">
          <Form.Label>Priority</Form.Label>
          <Form.Select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            disabled={submitting}
          >
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </Form.Select>
        </Form.Group>

        {/* Source Selection */}
        <Form.Group className="mb-3">
          <Form.Label>Source</Form.Label>
          <Form.Select
            name="source"
            value={formData.source}
            onChange={handleChange}
            disabled={submitting}
          >
            <option value="Referral">Referral</option>
            <option value="Website">Website</option>
            <option value="Event">Event</option>
          </Form.Select>
        </Form.Group>

        {/* Status Selection */}
        <Form.Group className="mb-3">
          <Form.Label>Status</Form.Label>
          <Form.Select
            name="status"
            value={formData.status}
            onChange={handleChange}
            disabled={submitting}
          >
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
            <option value="Converted">Converted</option>
          </Form.Select>
        </Form.Group>

        {/* Assigned User Selection */}
        <Form.Group className="mb-3">
          <Form.Label>Assigned To</Form.Label>
          <Form.Select
            name="assigned_to"
            value={formData.assigned_to}
            onChange={handleChange}
            required
            disabled={submitting}
          >
            <option value="">Select User</option>
            {users.map(user => (
              <option key={user.user_id} value={user.user_id}>
                {user.username}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          className="mt-3 w-100"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" /> Saving...
            </>
          ) : (
            'Add Lead'
          )}
        </Button>
      </Form>
    </div>
  );
};

export default AddLead;
